/**
 * 記事やウェブページ、コンテンツデータを複製します。
 *
 * Updated: 2024/01/12
 *
 * @requires jQuery
 * @param {Object} options
 * @param {Function} options.beforeSubmit 複製を実行する前に処理を挟みたいときは、このオプションに関数をセットします。
 * @param {Boolean} options.changeSite 複製するオブジェクトが記事の場合は `true` をセットするとブログの変更を可能にします。
 * @param {Boolean} options.changeBlog changeSite オプションの後方互換（非推奨）
 * @param {String} options.additionalText 複製元のタイトルにつけるテキストを設定します（初期値：'のコピー'）。
 * @param {String} options.basenameFieldName アーカイブマッピングで出力されるファイル名に利用しているフィールドの name 属性値を指定します。（初期値：'basename'）。
 * @param {String} options.basenameAdditionalText basenameFieldName オプションで指定したフィールドの値に付け足すテキストを設定します（初期値：'-copy'）。
 * @param {String} options.contentDataTitleFieldName 複製するオブジェクトがコンテンツデータの場合は「〜のコピー」を付与したいフィールドの name 属性値をセットします。
 * @param {String} options.contentDataTitleFieldLabel 複製するオブジェクトがコンテンツデータの場合は「〜のコピー」を付与したいフィールドのラベルをセットします。
 * @param {String} options.contentDataChangeSite 複製するオブジェクトがコンテンツデータの場合にサイトの変更を有効にする場合
 */
import { checkIfApplied } from './checkIfApplied.js';
import { makeWidget } from './makeWidget.js';
import { getCookie } from './getCookie.js';
import { loadingImage } from './loadingImage.js';
import { setCookie } from './setCookie.js';
import { modalMsg } from './modalMsg.js';

export function duplicate(options = {}) {
    if (checkIfApplied('duplicate')) return;

    const dataType = window.mtappVars.type;
    if (!['entry', 'page', 'content_data'].includes(dataType)) {
        return;
    }

    const defaultBasenameField = mtappVars.screen_id === 'edit-content-type-data' ? 'identifier' : 'basename';
    const op = {
        beforeSubmit: null,
        changeSite: false,
        changeBlog: false,
        additionalText: 'のコピー',
        basenameFieldName: defaultBasenameField,
        basenameAdditionalText: '-copy',
        contentDataTitleFieldName: 'data_label',
        contentDataTitleFieldLabel: 'タイトル',
        contentDataChangeSite: [],
        ...options
    };

    // 後方互換
    op.changeBlog = op.changeBlog || op.changeSite;

    // サイトをまたいだ複製を可能とするかどうか
    let crossSiteDuplicationType = '';
    if (dataType === 'entry' && op.changeBlog) {
        crossSiteDuplicationType = 'entry';
    } else if (dataType === 'page' && op.changeBlog) {
        crossSiteDuplicationType = 'page';
    } else if (dataType === 'content_data' && op.contentDataChangeSite.length > 0) {
        crossSiteDuplicationType = 'cd';
    }

    // モーダルを初期化
    const { modal, modalSubmit } = initModal();

    // 複製ボタンを作成
    const duplicateButton = initDuplicateButton(modal);

    // サイトの変更が有効の場合のドロップダウンリストと複製ボタンを追加
    let changeSiteSelector = createChangeBlogSelector(crossSiteDuplicationType, op);
    if (crossSiteDuplicationType !== '' && changeSiteSelector === null) {
        console.error('An error occurred. Please check the settings of the MTAppjQuery plugin.');
        console.error({
            crossSiteDuplicationType,
            changeSiteSelector
        });
        return;
    }

    // #entry-publishing-widget ウィジェットを取得
    const widgetContainer = document.getElementById('entry-publishing-widget');
    if (!widgetContainer) {
        console.error('Entry publishing widget not found.');
        return;
    }

    // 複製ボタン・ウィジェットをページに追加
    if (crossSiteDuplicationType !== '' && changeSiteSelector !== null) {
        // 複製ウィジェットのHTMLを作成
        const newWidgetHtml = makeWidget({
            label: '複製',
            basename: 'mtapp-duplicate',
            content: `
                <div id="mtapp-duplicate-widget-content" class="form-group">
                    <label for="mtapp-duplicate-blog-id" class="form-label">サイトを変更</label>
                </div>
            `
        });
        // 複製ウィジェットのElementを作成
        const newWidget = document.createElement('div');
        newWidget.innerHTML = newWidgetHtml;
        // 複製ウィジェットを挿入
        widgetContainer.insertAdjacentElement('afterend', newWidget);
        // 複製ウィジェットに複製ボタンを追加
        const newWidgetContent = document.getElementById('mtapp-duplicate-widget-content');
        newWidgetContent.appendChild(changeSiteSelector);
        newWidgetContent.appendChild(duplicateButton);
    } else {
        const lastPrimaryButton = widgetContainer.querySelector('.btn-primary[name="status"]');
        if (lastPrimaryButton) {
            lastPrimaryButton.parentElement.appendChild(duplicateButton);
        }
    }

    // メインの form を取得
    const form = duplicateButton.closest('form');

    // タイトルフィールドを取得
    const orgTitleField =
        dataType === 'content_data'
            ? document.querySelector(`[name="${op.contentDataTitleFieldName}"]`)
            : document.querySelector('[name="title"]');

    // 複製後のタイトルを設定するフィールドのラベルを取得
    const newTitleLabel = document.getElementById('mtapp-modal-duplicate-title-label');

    // 複製後のタイトルを設定するフィールドを取得
    const newTitleField = document.getElementById('mtapp-modal-duplicate-new-title');

    let inputId = document.querySelector('[name="id"]');
    const inputBlogId = document.querySelector('[name="blog_id"]');
    const inputContentTypeId = document.querySelector('[name="content_type_id"]');
    const inputReturnArgs = document.querySelector('[name="return_args"]');
    const inputStatus = document.querySelector('[name="status"]');
    const identifier = document.querySelector('[name="identifier"]');
    const basenameField = document.querySelector(`[name="${op.basenameFieldName}"]`);

    // エラーなどで出力ファイル名が消える場合の対応
    if (dataType === 'content_data' && identifier && identifier.value === '') {
        const orgIdentifier = getCookie('mtapp_duplicate_identifier');
        if (orgIdentifier) {
            identifier.value = orgIdentifier;
            setCookie('mtapp_duplicate_identifier', '');
        }
    }

    // 複製ボタンをクリックした時
    duplicateButton.addEventListener('click', function () {
        if (newTitleLabel) {
            newTitleLabel.innerHTML = op.contentDataTitleFieldLabel;
        }
        // 元のタイトルを複製後のタイトルにセット
        newTitleField.value = `${orgTitleField.value}${op.additionalText}`;
        modal.show();
    });

    // ブロックエディタ、MTブロックエディタへの対応
    const saveBlockEditors = function () {
        // ブロックエディタの保存
        if (typeof window?.jQuery?.updateblock === 'function') {
            window.jQuery.updateblock();
        }

        // MTブロックエディタの保存（MT本体の resetMultiLineTextData(), setMultiLineTextData() を参照）
        const $form = jQuery('#edit-content-type-data-form');
        // resetMultiLineTextData
        $form.find('textarea[name^=content-field-multi-]').remove();
        // setMultiLineTextData
        let fieldId, m;
        $form.find('textarea[name^=content-field-]').each(function () {
            m = this.name.match(/-(\d+)$/);
            if (m) {
                fieldId = m[1];
                if (jQuery(this).attr('data-full_rich_text') && typeof window.app !== 'undefined') {
                    window.app.editors['editor-input-content-field-' + fieldId].save();
                }

                // archetype_editor
                if (jQuery('[name="editor-input-content-field-' + fieldId + '"]').length > 0) {
                    this.value = jQuery('[name="editor-input-content-field-' + fieldId + '"]').val();
                }
                $form.append(
                    jQuery('<textarea hidden style="display:none;" name="content-field-multi-' + fieldId + '" mt:raw-name="1"></textarea>')
                );
                $form.find('textarea[name=content-field-multi-' + fieldId + ']').val(this.value);
            }
        });
    };

    // モーダル内の複製ボタンがクリックされた時の処理
    modalSubmit.addEventListener('click', function () {
        if (!window.confirm('複製しますか？')) {
            return;
        }

        // ローディング画像を表示
        loadingImage('show');

        // ブロックエディタの保存
        saveBlockEditors();

        // 出力ファイル名に使われるフィールドにテキストを追加
        basenameField.value = basenameField.value + op.basenameAdditionalText;
        if (op.basenameFieldName === 'basename') {
            const basenameManual = document.querySelector('[name="basename_manual"]');
            if (basenameManual) {
                basenameManual.value = '1';
            }
        }

        // タイトルフィールドに変更後のタイトルをセット
        orgTitleField.value = newTitleField.value;

        // 既存の id を削除して、新しい author_id をセット
        if (inputId) {
            const hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = 'author_id';
            hiddenInput.value = mtappVars.author_id;
            inputId.insertAdjacentElement('afterend', hiddenInput);
            inputId.parentNode.removeChild(inputId);
            inputId = undefined;
        }

        // ブログ ID をセット
        let orgSiteId, destSiteId;
        if (crossSiteDuplicationType !== '' && inputBlogId) {
            orgSiteId = Number(inputBlogId.value);
            destSiteId = Number(changeSiteSelector.value) || orgSiteId;
            inputBlogId.value = destSiteId;
        }

        // return_args をセット
        if (inputReturnArgs) {
            inputReturnArgs.value = inputReturnArgs.value.replace(/&amp;id=[0-9]+|&id=[0-9]+/, '');
        }

        // status を下書きにセット
        if (inputStatus) {
            const statusOption = inputStatus.querySelector('option[value="1"]');
            if (statusOption) {
                inputStatus.value = '1';
            }
        }

        // コンテンツデータでサイト変更を可能にする場合
        if (crossSiteDuplicationType === 'cd') {
            // コンテンツタイプ ID をセット
            let orgContentTypeId, destContentTypeId;
            if (dataType === 'content_data' && inputContentTypeId) {
                orgContentTypeId = Number(inputContentTypeId.value);
                const selectedValue = changeSiteSelector.value;
                const selectedOption = changeSiteSelector.querySelector(`option[value="${selectedValue}"]`);
                destContentTypeId =
                    selectedOption && selectedOption.dataset.contentTypeId
                        ? Number(selectedOption.dataset.contentTypeId)
                        : orgContentTypeId;
                inputContentTypeId.value = destContentTypeId;
            }

            if (orgContentTypeId !== destContentTypeId) {
                // リッチエディタを保存してから、リッチエディタに対してMTの処理が走らないようにする
                Object.keys(window.app.editors).forEach((id) => {
                    window.app.editors[id].currentEditor.save();
                    delete window.app.editors[id];
                });

                // Removing classes and attributes
                const multiLineFields = document.querySelectorAll('.multi_line_text-field-container');
                multiLineFields.forEach((field) => field.classList.remove('multi_line_text-field-container'));

                const richTextAttributes = document.querySelectorAll('[data-full_rich_text]');
                richTextAttributes.forEach((attr) => attr.removeAttribute('data-full_rich_text'));

                const orgContentFields = getContentFields(orgContentTypeId, orgSiteId);
                const destContentFields = getContentFields(destContentTypeId, destSiteId);

                const orgFieldLabelIdMapping = {};
                const fieldTypes = {};
                const fieldIdMapping = {};
                const categorySetMapping = {};

                // コピー元のサイトのコンテンツフィールドを取得
                for (const field of orgContentFields) {
                    orgFieldLabelIdMapping[field.label] = field.id;
                    fieldTypes[field.id] = field.type;
                    if (field.type === 'categories') {
                        categorySetMapping[field.id] = Number(field.options.category_set);
                    }
                }

                // コピー先のコンテンツフィールドを取得
                for (const field of destContentFields) {
                    // 旧フィールド ID がキー、新フィールド ID が値の連想配列を作成
                    if (typeof orgFieldLabelIdMapping[field.label] !== 'undefined') {
                        fieldIdMapping[orgFieldLabelIdMapping[field.label]] = field.id;
                    }
                    if (field.type === 'categories') {
                        categorySetMapping[field.id] = Number(field.options.category_set);
                    }
                }

                // name によってその値や name 値を変更して submit する
                const fields = document.querySelectorAll('[name]');
                for (const field of fields) {
                    const nameAttr = String(field.name);
                    const matched = nameAttr.match(/-(\d+)/);
                    if (matched) {
                        const orgFieldId = Number(matched[1]);
                        if (typeof fieldIdMapping[orgFieldId] === 'undefined') {
                            continue;
                        }
                        const destFieldId = Number(fieldIdMapping[orgFieldId]);
                        const replacement = '-' + destFieldId;
                        field.name = nameAttr.replace(/-\d+/i, replacement);
                        // カテゴリフィールドの場合
                        if (fieldTypes[orgFieldId] === 'categories') {
                            const orgCategorySetId = categorySetMapping[orgFieldId] - 0;
                            const destCategorySetId = categorySetMapping[destFieldId] - 0;
                            if (orgCategorySetId !== destCategorySetId) {
                                field.disabled = true;
                            }
                        }
                    } else if (nameAttr === 'block_editor_data') {
                        let json = field.value;
                        json = json.replace(/-field-(\d+)/gi, function (match, id) {
                            if (typeof fieldIdMapping[id] === 'undefined') {
                                return match;
                            }
                            return '-field-' + fieldIdMapping[id];
                        });
                        field.value = json;
                    }
                    setCookie('mtapp_duplicate_identifier', identifier.value, 7);
                }
            }
        }

        // 複製前の処理
        if (typeof op.beforeSubmit === 'function') {
            op.beforeSubmit.call(window);
        }
        form.submit();
    });
}

/**
 * モーダルを初期化します。
 *
 * @returns {{modalSubmit: HTMLElement, modalCancel: HTMLElement, modal: bootstrap.Modal}}
 */
function initModal() {
    // モーダルを作成
    const modalElement = document.getElementById('mtapp-modal-duplicate');
    const modal = new bootstrap.Modal(modalElement);

    // モーダル上の複製ボタンを取得
    const modalSubmit = document.getElementById('mtapp-modal-duplicate-submit'); // 複製

    // モーダル上のキャンセルボタンを取得/クリックされたらモーダルを閉じる
    const modalCancel = document.getElementById('mtapp-modal-duplicate-cancel');
    modalCancel.addEventListener('click', function () {
        modal.hide();
    });

    return {
        modal,
        modalSubmit,
        modalCancel
    };
}

/**
 * 複製ボタンを作成します。
 *
 * @param modal
 * @returns {HTMLButtonElement}
 */
function initDuplicateButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'mtapp-duplicate-button';
    button.className = 'btn btn-warning text-white';
    button.textContent = '複製';
    return button;
}

/**
 * エラーメッセージを表示します。
 */
function showModalErrorMsg() {
    modalMsg({
        type: 'error',
        title: '設定エラー',
        content: `
            <p class="fw-bold">システムスコープの MTAppjQuery プラグインの設定で下記を有効にしてください。</p>
            <ul>
                <li>「利用データの設定」の「アクセス可能なサイトのデータ」（簡易モード）</li>
                <li>「利用データの設定」の「パーミッションのデータ」</li>
            </ul>
            <p class="fw-bold">また、ユーザーに明示的に権限を与える必要があります。</p>
        `
    });
}

/**
 * サイトを変更するドロップダウンリストのオプションを作成します。
 *
 * @param crossSiteDuplicationType
 * @param options
 * @returns {HTMLSelectElement|null}
 */
function createChangeBlogSelector(crossSiteDuplicationType, options) {
    if (!crossSiteDuplicationType) {
        return null;
    }

    const selectElement = document.createElement('select');
    selectElement.className = 'form-select mb-3';
    selectElement.id = 'mtapp-duplicate-blog-id';

    if (crossSiteDuplicationType === 'entry' || crossSiteDuplicationType === 'page') {
        let permittedBlogs;
        if (crossSiteDuplicationType === 'entry') {
            permittedBlogs = mtappVars.me.permission.can_create_post_blogs.map((blog) => Number(blog));
        } else if (crossSiteDuplicationType === 'page') {
            permittedBlogs = mtappVars.me.permission.can_manage_pages_blogs.map((blog) => Number(blog));
        }

        if (
            Array.isArray(permittedBlogs) &&
            permittedBlogs.length &&
            Array.isArray(mtappVars.permitted_sites) &&
            mtappVars.permitted_sites.length
        ) {
            mtappVars.permitted_sites.forEach((site) => {
                const id = Number(site.id);
                if (permittedBlogs.includes(id)) {
                    const isSelected = id === mtappVars.blog_id;
                    selectElement.innerHTML += `<option ${isSelected ? 'selected' : ''} value="${id}">${site.name}</option>`;
                }
            });
        } else {
            showModalErrorMsg();
            return null;
        }
    } else if (crossSiteDuplicationType === 'cd') {
        options.contentDataChangeSite.forEach((site) => {
            const selected = site.id === mtappVars.blog_id ? 'selected' : '';
            selectElement.innerHTML += `<option ${selected} value="${site.id}" data-content-type-id="${site.contentTypeId}">${site.name}</option>`;
        });
    }

    if (selectElement.innerHTML) {
        return selectElement;
    }
    return null;
}

/**
 * mtappVars.contentTypes から id と site_id を指定して該当するオブジェクトの fields を取得する
 *
 * @param {Number} id
 * @param {Number} siteId
 * @returns {*}
 */
function getContentFields(id, siteId) {
    const currentContentType = window.mtappVars.contentTypes.find((contentType) => contentType.id === id && contentType.site_id === siteId);
    if (currentContentType) {
        return currentContentType.fields;
    }
    return null;
}
